import * as React from 'react';
import tw, { styled } from 'twin.macro';
import { useTranslation } from 'next-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { fromUnixTime } from 'date-fns';
import { useAtom, useSetAtom, atom } from 'jotai';
import { TrashIcon } from '@heroicons/react/24/outline';
import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { formatDateToDayMonthYear, addDaysToDayMonthYear, useAnalytics } from '@restworld/utils-common';
import { Alert, BaseModal, ButtonV2, FlexibleSlotCard, Title } from '@restworld/ui-ds';
import { StripeSubscription } from '@restworld/utility-types';
import { useAuth } from '@restworld/feature-authentication';

import {
  useFlexibleSubscriptionSlots,
  useFlexibleSubscriptionSlotsQuery,
  subscriptionSlotQueryKey,
  useFetchIsSubscriptionFeedbacksSubmittedQuery,
  useIsSubscriptionFeedbacksSubmitted,
  isSubscriptionFeedbacksSubmittedQueryKey,
  useCancelFlexibleSubscriptionMutation,
} from '../flexible-slots-v2/flexible-slots-v2.hooks';
import SubscriptionFeedbackModal from '../SubscriptionFeedbackModal';

const ppuCancelConfirmationModalAtom = atom<boolean>(false);
const ppuCancelFeedbackModalAtom = atom<boolean>(false);
const ppuSelectedSubscriptionAtom = atom<Partial<StripeSubscription>>({});

const PpuSubscriptionSlots: React.FunctionComponent = () => {
  const queryClient = useQueryClient();
  const [isFeedbackModal, feedbackModalToggler] = useAtom(ppuCancelFeedbackModalAtom);
  const [selectedSubscription, setSelectedSubscription] = useAtom(ppuSelectedSubscriptionAtom);

  useFlexibleSubscriptionSlotsQuery();
  useFetchIsSubscriptionFeedbacksSubmittedQuery();
  const subscriptionSlots = useFlexibleSubscriptionSlots();

  const activeSubscriptions = React.useMemo(
    () => subscriptionSlots?.filter((s) => s.status === 'active') ?? [],
    [subscriptionSlots]
  );

  const totalSlotCount = React.useMemo(
    () => activeSubscriptions.reduce((acc, s) => acc + (s.items?.[0]?.quantity || 1), 0),
    [activeSubscriptions]
  );

  const onRefresh = React.useCallback(() => {
    queryClient.invalidateQueries({ queryKey: subscriptionSlotQueryKey });
    queryClient.invalidateQueries({ queryKey: isSubscriptionFeedbacksSubmittedQueryKey });
    setSelectedSubscription({});
  }, [queryClient, setSelectedSubscription]);

  if (!activeSubscriptions.length) {
    return null;
  }

  return (
    <>
      <PpuSubscriptionSlotsContent activeSubscriptions={activeSubscriptions} totalSlotCount={totalSlotCount} />
      <PpuCancelConfirmationModal />
      <SubscriptionFeedbackModal
        isOpen={isFeedbackModal}
        onClose={() => feedbackModalToggler(false)}
        stripeSubscriptionId={selectedSubscription?.id}
        refresh={onRefresh}
      />
    </>
  );
};

interface PpuSubscriptionSlotsContentProps {
  activeSubscriptions: StripeSubscription[];
  totalSlotCount: number;
}

const PpuSubscriptionSlotsContent: React.FunctionComponent<PpuSubscriptionSlotsContentProps> = ({
  activeSubscriptions,
  totalSlotCount,
}) => {
  const { t } = useTranslation('payment-and-invoices');
  const { trackEvent } = useAnalytics();
  const { user } = useAuth();
  const setIsOpen = useSetAtom(ppuCancelConfirmationModalAtom);
  const setSelectedSubscription = useSetAtom(ppuSelectedSubscriptionAtom);

  return (
    <SectionWrapper>
      <HeaderWrapper>
        <Title
          fontSize="large"
          fontWeight="semibold"
          content={t('annual_subscription_title')}
          textColor="neutral-700"
        />
      </HeaderWrapper>
      <ContentWrapper>
        <div tw="self-stretch justify-start items-start gap-2 inline-flex">
          <Title
            content={t('annual_slots_active', { count: totalSlotCount })}
            fontSize="base"
            fontWeight="semibold"
            textColor="neutral-700"
          />
        </div>
        <ul tw="self-stretch flex-col justify-start items-start gap-4 flex">
          {activeSubscriptions.map((sub, i) => {
            const date = sub.current_period_end
              ? formatDateToDayMonthYear(fromUnixTime(sub.current_period_end).toISOString())
              : '';
            const toBeCancelledAtPeriodEnd = sub.cancel_at_period_end || !!sub.cancel_at;
            const slots = sub.items?.[0]?.quantity || 1;

            return (
              <li key={`annual-sub-${sub.id || i}`} tw="self-stretch">
                <FlexibleSlotCard
                  status="active"
                  title={t('subscription_slot')}
                  dateLabel={t('next_payment_on')}
                  date={date}
                  cancelAt={sub.cancel_at}
                  currentPeriodEnd={sub.current_period_end}
                  orderNumber={slots}
                  onSlotCloseCallback={() => {
                    trackEvent('employer_cancel_slot', {
                      email: user?.email,
                      phone: user?.phone_number,
                    });
                    setSelectedSubscription(sub);
                    setIsOpen(true);
                  }}
                  showCrossIcon={!toBeCancelledAtPeriodEnd}
                  canceledAtPeriodEnd={toBeCancelledAtPeriodEnd}
                />
              </li>
            );
          })}
        </ul>
      </ContentWrapper>
    </SectionWrapper>
  );
};

const PpuCancelConfirmationModal = () => {
  const [isOpen, setIsOpen] = useAtom(ppuCancelConfirmationModalAtom);
  const [selectedSubscription, setSelectedSubscription] = useAtom(ppuSelectedSubscriptionAtom);
  const [, feedbackModalToggler] = useAtom(ppuCancelFeedbackModalAtom);
  const { t } = useTranslation('payment-and-invoices');

  const {
    mutate: mutateCancelSubscription,
    isPending: isCancelPending,
    isSuccess: isCancelSuccess,
  } = useCancelFlexibleSubscriptionMutation();

  const isSubscriptionFeedbacksSubmitted = useIsSubscriptionFeedbacksSubmitted();
  const shouldShowFeedbackModal = !isSubscriptionFeedbacksSubmitted;

  const onCancel = React.useCallback(() => {
    if (shouldShowFeedbackModal) {
      feedbackModalToggler(true);
      setIsOpen(false);
    } else {
      mutateCancelSubscription({
        createFeedback: false,
        stripeSubscriptionId: selectedSubscription?.id as string,
      });
    }
  }, [selectedSubscription, shouldShowFeedbackModal, mutateCancelSubscription, feedbackModalToggler, setIsOpen]);

  const onModalClose = React.useCallback(() => {
    setIsOpen(false);
    setSelectedSubscription({});
  }, [setIsOpen, setSelectedSubscription]);

  React.useEffect(() => {
    if (isCancelSuccess) onModalClose();
  }, [isCancelSuccess, onModalClose]);

  const expiresAt = React.useMemo(
    () =>
      selectedSubscription?.current_period_end
        ? formatDateToDayMonthYear(fromUnixTime(selectedSubscription.current_period_end).toISOString())
        : '',
    [selectedSubscription]
  );

  const footerEl = (
    <div tw="flex flex-col justify-center items-center gap-5 w-full pt-[64px] lg:(flex-row justify-between)">
      <ButtonV2
        onClick={onCancel}
        fullwidth
        loading={isCancelPending}
        EndIcon={shouldShowFeedbackModal ? undefined : TrashIcon}
        variant={shouldShowFeedbackModal ? 'primary' : 'secondary-danger'}
      >
        {shouldShowFeedbackModal ? t('review_and_cancel') : t('cancel_slot_key')}
      </ButtonV2>
      <ButtonV2 variant="tertiary" fullwidth onClick={() => setIsOpen(false)}>
        {t('do_not_cancel')}
      </ButtonV2>
    </div>
  );

  const titleEl = (
    <Title
      content={t('do_you_really_want_to_cancel_the_slot')}
      fontSize="extraLarge"
      fontWeight="semibold"
      textColor="neutral-700"
      twStyle={tw`text-center leading-[30px] pb-6`}
    />
  );

  return (
    <BaseModal
      showMobileGestures
      modalStyles={[tw`w-full max-w-[640px] lg:(max-w-screen-sm)`]}
      open={isOpen}
      onClose={onModalClose}
      title={titleEl}
      footer={footerEl}
    >
      <div tw="flex flex-col justify-center items-center gap-5 w-full text-center">
        <Title
          content={t('by_proceeding_the_slot_will_be_deactivated_starting_date', {
            expiresAt: addDaysToDayMonthYear(expiresAt),
          })}
          fontSize="base"
          fontWeight="medium"
          textColor="neutral-700"
        />
        <Alert
          variant="restworld"
          text={t('annual_subscription_cancel_alert')}
          icon={<ExclamationTriangleIcon className="h-4 w-4 min-h-[16px] min-w-[16px]" />}
          wrapperStyling={tw`justify-start text-left mt-3`}
        />
      </div>
    </BaseModal>
  );
};

export default PpuSubscriptionSlots;

const SectionWrapper = styled.div`
  ${tw`flex-col justify-start items-center gap-4 inline-flex w-full`}
`;

const HeaderWrapper = styled.div`
  ${tw`self-stretch h-10 justify-between items-center inline-flex`}
`;

const ContentWrapper = styled.div`
  ${tw`self-stretch p-5 rounded-2xl border border-neutral-40 flex-col justify-start items-start gap-10 flex overflow-y-auto`}
`;
